import { Reducer, ReducersMapObject } from "@reduxjs/toolkit";
import { persistReducer } from "redux-persist";
import { PersistPartial } from "redux-persist/es/persistReducer";

import {
  AuthState,
  CreateUserState,
  ModalState,
  ProfileState,
  RootState,
  SettingsState,
  DocumentsState,
  OnboardState,
  PropertiesState,
  OnboardPropertyState,
  SigningState,
  SinglePropertyState,
  ChatState,
  TapeChartState,
  TaskState,
  OrganizationState,
  OrganizationAdminState,
  ReportsState,
  ReservationTypeMappingsState,
  StatementsState,
  AdminAssignmentState,
  NotificationPreferencesState,
} from "@/types/store";
import {
  authPersistConfig,
  onboardPersistConfig,
  onboardPropertyPersistConfig,
} from "@/store/persistConfigs";

import AuthSlice from "./authSlice";
import OnboardSlice from "./onboardSlice";
import OnboardPropertySlice from "./onboardPropertySlice";
import CreateUserSlice from "./createUserSlice";
import ProfileSlice from "./profileSlice";
import ModalSlice from "./modalSlice";
import SettingsSlice from "./settingsSlice";
import PropertiesSlice from "./propertiesSlice";
import DocumentsSlice from "./documentsSlice";
import SigningSlice from "./signingSlice";
import SinglePropertySlice from "./singlePropertiesSlice";
import ChatSlice from "./chatSlice";
import SupportSlice from "./supportSlice";
import TapeChartSlice from "./tapeChartSlice";
import TaskSlice from "./taskSlice";
import OrganizationSlice from "./organizationSlice";
import OrganizationAdminSlice from "./organizationAdminSlice";
import ReportsSlice from "./reportsSlice";
import ReservationTypeMappingsSlice from "./reservationTypeMappingsSlice";
import StatementsSlice from "./statementsSlice";
import AdminAssignmentSlice from "./adminAssignmentSlice";
import NotificationPreferencesSlice from "./notificationPreferencesSlice";

type PersistRootState = RootState;

interface SliceReducers extends ReducersMapObject<PersistRootState> {
  Auth: Reducer<AuthState & PersistPartial>;
  Onboard: Reducer<OnboardState & PersistPartial>;
  OnboardProperty: Reducer<OnboardPropertyState & PersistPartial>;
  Documents: Reducer<DocumentsState>;
  Modal: Reducer<ModalState>;
  CreateUser: Reducer<CreateUserState>;
  Profile: Reducer<ProfileState>;
  Settings: Reducer<SettingsState>;
  Properties: Reducer<PropertiesState>;
  Signing: Reducer<SigningState>;
  SingleProperty: Reducer<SinglePropertyState>;
  Chat: Reducer<ChatState>;
  TapeChart: Reducer<TapeChartState>;
  Task: Reducer<TaskState>;
  Organization: Reducer<OrganizationState>;
  OrganizationAdmin: Reducer<OrganizationAdminState>;
  Reports: Reducer<ReportsState>;
  ReservationTypeMappings: Reducer<ReservationTypeMappingsState>;
  Statements: Reducer<StatementsState>;
  AdminAssignment: Reducer<AdminAssignmentState>;
  NotificationPreferences: Reducer<NotificationPreferencesState>;
}

export const rootReducer: SliceReducers = {
  Auth: persistReducer(authPersistConfig, AuthSlice),
  Onboard: persistReducer(onboardPersistConfig, OnboardSlice),
  OnboardProperty: persistReducer(
    onboardPropertyPersistConfig,
    OnboardPropertySlice
  ),
  Documents: DocumentsSlice,
  Modal: ModalSlice,
  CreateUser: CreateUserSlice,
  Profile: ProfileSlice,
  Settings: SettingsSlice,
  Properties: PropertiesSlice,
  Signing: SigningSlice,
  SingleProperty: SinglePropertySlice,
  Chat: ChatSlice,
  Support: SupportSlice,
  TapeChart: TapeChartSlice,
  Task: TaskSlice,
  Organization: OrganizationSlice,
  OrganizationAdmin: OrganizationAdminSlice,
  Reports: ReportsSlice,
  ReservationTypeMappings: ReservationTypeMappingsSlice,
  Statements: StatementsSlice,
  AdminAssignment: AdminAssignmentSlice,
  NotificationPreferences: NotificationPreferencesSlice,
};
